import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Avatar,
  Button,
  Menu,
  MenuDivider,
  MenuGroupHeader,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import { useApi } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { LegendsLogo } from '../brand/LegendsLogo';
import { LEGENDS } from '../brand/theme';
import type { ThemeName } from './theme';
import { DownloadNotice } from '../ui/DownloadNotice';
import { config } from '../config';

const RUN_SHEET_URL = config.envProfile.id === 'legends-trn' ? '/legends-run-sheet.html' : '/run-sheet.html';

const useStyles = makeStyles({
  root: {
    minHeight: '100%',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: tokens.colorNeutralBackground2,
  },
  // Masthead: white with a blue rule (light skin), navy gradient with a blue rule (dark skin).
  mast: {
    display: 'flex',
    alignItems: 'center',
    gap: '24px',
    padding: '10px 28px',
    position: 'sticky',
    top: 0,
    zIndex: 10,
  },
  mastLight: { backgroundColor: LEGENDS.white, borderBottom: `3px solid ${LEGENDS.blue}` },
  mastDark: {
    backgroundImage: 'linear-gradient(90deg, #0B131B, #14202C)',
    borderBottom: `2px solid ${LEGENDS.blue}`,
  },
  brandButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '22px',
    border: 'none',
    background: 'transparent',
    padding: 0,
    cursor: 'pointer',
    textAlign: 'left',
    color: 'inherit',
    marginRight: 'auto',
    minWidth: 0,
  },
  divider: { width: '1px', alignSelf: 'stretch', margin: '6px 0' },
  dividerLight: { backgroundColor: LEGENDS.light },
  dividerDark: { backgroundColor: LEGENDS.navyLine },
  titles: { display: 'flex', flexDirection: 'column', minWidth: 0 },
  product: { fontSize: '1.4rem', fontWeight: 700, lineHeight: 1.2, whiteSpace: 'nowrap' },
  productLight: { color: LEGENDS.blue },
  productDark: { color: LEGENDS.navyText },
  sub: { fontSize: tokens.fontSizeBase300, lineHeight: 1.3, whiteSpace: 'nowrap' },
  subLight: { color: LEGENDS.text },
  subDark: { color: LEGENDS.navyMuted },
  tag: { fontSize: tokens.fontSizeBase200, fontStyle: 'italic', color: LEGENDS.blue, lineHeight: 1.3 },
  persona: { textAlign: 'right', lineHeight: 1.3, fontSize: tokens.fontSizeBase200 },
  personaName: { display: 'block', fontSize: tokens.fontSizeBase300, fontWeight: 700 },
  personaLight: { color: LEGENDS.text },
  personaDark: { color: LEGENDS.navyMuted },
  personaNameLight: { color: LEGENDS.dense },
  personaNameDark: { color: LEGENDS.navyText },
  // Scope strip under the masthead (dark skin's banner, both themes).
  scope: {
    display: 'flex',
    gap: '26px',
    alignItems: 'center',
    flexWrap: 'wrap',
    padding: '7px 28px',
    fontSize: tokens.fontSizeBase300,
    lineHeight: 1.3,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  scopeLight: { backgroundColor: LEGENDS.blueBg, color: LEGENDS.dense },
  scopeDark: { backgroundColor: '#10283A', color: LEGENDS.navyText },
  scopeKey: {
    color: LEGENDS.blue,
    fontWeight: 700,
    textTransform: 'uppercase',
    fontSize: tokens.fontSizeBase100,
    letterSpacing: '.06em',
    marginRight: '8px',
  },
  scopeKeyLight: { color: LEGENDS.blueDark },
  scopeVal: { fontWeight: 700, fontVariantNumeric: 'tabular-nums' },
  main: {
    flex: 1,
    padding: '24px 28px 40px',
    maxWidth: '1280px',
    width: '100%',
    boxSizing: 'border-box',
    margin: '0 auto',
  },
});

const IN_FLIGHT = [
  'Draft',
  'Submitted',
  'InReview',
  'Approved',
  'Provisioning',
  'NeedsAttention',
  'Validating',
];

export function AppShell({
  theme,
  onToggleTheme,
  children,
}: {
  theme: ThemeName;
  onToggleTheme: () => void;
  children: ReactNode;
}) {
  const styles = useStyles();
  const dark = theme === 'dark';
  const { user, switchPersona, signOut } = useAuth();
  const api = useApi();
  const navigate = useNavigate();
  const requests = useQuery({
    queryKey: ['requests'],
    queryFn: () => api.listRequests(),
    refetchInterval: 10_000,
  });
  const venues = useQuery({ queryKey: ['venues'], queryFn: () => api.listVenues(), refetchInterval: 15_000 });
  const templates = useQuery({ queryKey: ['templates'], queryFn: () => api.listTemplates() });

  const list = requests.data ?? [];
  const count = (statuses: string[]) => list.filter((r) => statuses.includes(r.status)).length;
  const scopeItems: Array<[string, string | number]> = [
    ['Templates', templates.data?.map((t) => t.id).join(' · ') ?? 'LTPL'],
    ['Venues live in D365', venues.data?.length ?? '—'],
    ['In flight', count(IN_FLIGHT)],
    ['Awaiting approval', count(['InReview', 'Approved'])],
    ['Provisioning', count(['Provisioning', 'NeedsAttention', 'Validating'])],
  ];

  return (
    <div className={styles.root}>
      <header className={mergeClasses(styles.mast, dark ? styles.mastDark : styles.mastLight)}>
        <button
          type="button"
          className={styles.brandButton}
          onClick={() => navigate('/')}
          aria-label="Venue requests home"
        >
          <LegendsLogo tone={dark ? 'onDark' : 'onLight'} height={46} />
          <span className={mergeClasses(styles.divider, dark ? styles.dividerDark : styles.dividerLight)} />
          <span className={styles.titles}>
            <span className={mergeClasses(styles.product, dark ? styles.productDark : styles.productLight)}>
              Venue onboarding
            </span>
            <span className={mergeClasses(styles.sub, dark ? styles.subDark : styles.subLight)}>
              Signed contract to ready-to-transact venue in Dynamics 365
            </span>
            {dark && <span className={styles.tag}>Venue factory command</span>}
          </span>
        </button>
        <span className={mergeClasses(styles.persona, dark ? styles.personaDark : styles.personaLight)}>
          <b
            className={mergeClasses(
              styles.personaName,
              dark ? styles.personaNameDark : styles.personaNameLight,
            )}
          >
            {user.name}
          </b>
          {user.roles.join(' · ')}
        </span>
        <Button appearance="subtle" onClick={() => navigate('/testing')}>
          Readiness testing
        </Button>
        <Button appearance="subtle" onClick={onToggleTheme}>
          {dark ? 'Light theme' : 'Dark theme'}
        </Button>
        <Menu>
          <MenuTrigger disableButtonEnhancement>
            <Button
              appearance="subtle"
              icon={<Avatar name={user.name} size={32} color="brand" />}
              aria-label="Account"
            />
          </MenuTrigger>
          <MenuPopover>
            <MenuList>
              <MenuGroupHeader>{user.upn}</MenuGroupHeader>
              <MenuDivider />
              {switchPersona && <MenuItem onClick={switchPersona}>Switch demo persona</MenuItem>}
              {signOut && <MenuItem onClick={signOut}>Sign out</MenuItem>}
              {api.resetDemo && (
                <MenuItem
                  onClick={() => {
                    api.resetDemo?.();
                    window.location.assign('/');
                  }}
                >
                  Reset demo data
                </MenuItem>
              )}
              {api.resetDemo && (
                <>
                  <MenuDivider />
                  {/* Presenter's demo script; static page in public/, opens in a new tab. The Legends
                      TRN site opens the Polaris run sheet (docs/DEMO_RUNSHEET.md). */}
                  <MenuItem onClick={() => window.open(RUN_SHEET_URL, '_blank', 'noopener')}>
                    Run sheet
                  </MenuItem>
                </>
              )}
            </MenuList>
          </MenuPopover>
        </Menu>
      </header>
      <div
        className={mergeClasses(styles.scope, dark ? styles.scopeDark : styles.scopeLight)}
        aria-label="Portfolio at a glance"
      >
        {scopeItems.map(([k, v]) => (
          <span key={k}>
            <span className={mergeClasses(styles.scopeKey, !dark && styles.scopeKeyLight)}>{k}</span>
            <span className={styles.scopeVal}>{v}</span>
          </span>
        ))}
      </div>
      <main className={styles.main}>{children}</main>
      <DownloadNotice />
    </div>
  );
}
